import { useCallback, useState, useEffect } from "react";
import { Workspace } from "../../../types/workspace";
import { CompanyFilter } from "../../../legacy/types/companyFilter";
import { PendingFiltersState } from "./FiltersContext";
import {
  FilterConflictType,
  getCurrentFilterTypes,
  hasFilterConflict,
  hasFilterTypeChanged,
  hasMarketConfig,
} from "../utils";
import { FILTER_TYPE_MAP } from "./constants";
import { useWorkspaceStore } from "../../../store/workspace/WorkspaceStore";

export const useFilterConflicts = (
  draft: Workspace | undefined,
  userFilters: CompanyFilter | undefined
) => {
  const globalFilterConflicts = useWorkspaceStore(
    (state) => state.globalFilterConflictTypes
  );
  const [shownConflictTypes, setShownConflictTypes] = useState<
    Set<FilterConflictType>
  >(new Set());

  const [pendingFilters, setPendingFilters] =
    useState<PendingFiltersState | null>(null);

  const [globalFilterConflictTypes, setGlobalFilterConflictTypes] = useState<
    Set<FilterConflictType>
  >(globalFilterConflicts);

  // Check for conflicts in filters
  const checkConflicts = useCallback(
    (filtersToApply: CompanyFilter) => {
      if (!draft) return null;

      const newFilterTypes = getCurrentFilterTypes(filtersToApply);
      const conflictingTypes = Array.from(newFilterTypes).filter((type) => {
        if (shownConflictTypes.has(type)) return false;
        return hasMarketConfig(draft, type);
      });

      if (conflictingTypes.length > 0) {
        const conflicts = hasFilterConflict(
          draft,
          filtersToApply,
          new Set(conflictingTypes)
        );
        return conflicts.length > 0 ? conflicts : null;
      }
      return null;
    },
    [draft, shownConflictTypes]
  );

  // Check for global filter changes
  const checkGlobalFilterChanges = useCallback(
    (oldFilters: Workspace | undefined, newFilters: Workspace | undefined) => {
      if (!oldFilters && !newFilters) {
        setGlobalFilterConflictTypes(new Set());
        return;
      }

      const allTypes = Object.values(FILTER_TYPE_MAP);
      const newModifiedTypes = new Set<FilterConflictType>();

      allTypes.forEach((type) => {
        if (hasFilterTypeChanged(type, oldFilters, newFilters)) {
          newModifiedTypes.add(type);
        }
      });

      setGlobalFilterConflictTypes(newModifiedTypes);
    },
    []
  );

  // Check for pending conflicts based on global changes
  useEffect(() => {
    if (!globalFilterConflictTypes.size || pendingFilters || !userFilters) {
      return;
    }

    const currentTypes = getCurrentFilterTypes(userFilters);
    const typesToCheck = new Set(
      Array.from(currentTypes).filter((type) =>
        globalFilterConflictTypes.has(type)
      )
    );

    if (typesToCheck.size === 0) {
      setGlobalFilterConflictTypes(new Set());
      return;
    }

    const globalConflicts = hasFilterConflict(draft, userFilters, typesToCheck);
    if (globalConflicts.length > 0) {
      const relevantConflicts = globalConflicts.filter((conflict) =>
        globalFilterConflictTypes.has(conflict.type)
      );
      if (relevantConflicts.length > 0) {
        setPendingFilters({
          filters: userFilters,
          conflicts: relevantConflicts,
        });
      }
    }
  }, [
    globalFilterConflictTypes,
    draft,
    userFilters,
    pendingFilters,
    shownConflictTypes,
  ]);

  const clearGlobalFilterConflicts = () => {
    setGlobalFilterConflictTypes(new Set());
  };

  return {
    shownConflictTypes,
    setShownConflictTypes,
    pendingFilters,
    setPendingFilters,
    checkConflicts,
    globalFilterConflictTypes,
    checkGlobalFilterChanges,
    clearGlobalFilterConflicts,
  };
};
